import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getJobs, updateJobInArray, removeJobById } from '../../../redux/features/jobSlice';
import { getWorkers } from '../../../redux/features/workerSlice';
import { getTeams } from '../../../redux/features/teamSlice';
import { getBusinessType } from '../../../utils/businessTypeUtils';
import { useWebSocket } from '../../../contexts/WebSocketContext';
import PulsingIndicator from '../../animation/PulsingIndicator';
import axios from 'axios';
import { 
  Users,
  Users2,
  Clock, 
  CheckCircle, 
  AlertCircle,
  AlertTriangle,
  MapPin,
  Coffee, 
  UserCheck,
  Wrench,
  X,
  Activity,
  ClipboardList,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Car,
  MoreHorizontal,
  Sun,
  Bed
} from 'lucide-react';
import { API_BASE_URL } from '../../../config/api.js';
import {
  formatDisplayValue,
  getJobDisplayStatus,
  isJobTimerActive,
  getJobsWithActiveTimer,
  isScheduledOpenJob,
  buildJobUpdatesFromWebSocket
} from '../../../utils/displayUtils';
import {
  isJobAssignedToWorker,
  getWorkerName,
  getWorkerInitials,
  getWorkerColor,
  countWorkerDayAssignments,
  getWorkerLoadColorClass,
  getWorkerMaxDailyInterventions,
  countScheduledJobsByHorizon,
  summarizeWorkerStatuses,
  summarizeTeamDailyLoad,
  getWorkerLoadBarColorClass,
  countOverloadedWorkers,
  isSameCalendarDay,
  isJobAssignedToTeam,
  countTeamDayAssignments,
  getTeamMaxDailyInterventions,
  getTeamMemberCount
} from '../../../utils/workerUtils';
import { formatDateKey } from '../../../utils/workerAvailabilityUtils';
import DatePickerField from '../../forms/job/DatePickerField';

const StatusView = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { jobs, loading } = useSelector((state) => state.job);
  
  // Debug: Log when jobs array changes
  useEffect(() => {
    console.log('🔄 StatusView: jobs array updated', {
      jobsCount: jobs?.length,
      jobsWithTimers: jobs?.filter(j => j.travelStartTime || j.workStartTime).map(j => ({
        id: j._id,
        clientName: j.clientName,
        status: j.status,
        travelStartTime: j.travelStartTime,
        workStartTime: j.workStartTime
      })),
      timestamp: new Date().toISOString()
    });
  }, [jobs]);
  const { workers } = useSelector((state) => state.worker || { workers: [] });
  const { teams = [] } = useSelector((state) => state.team || { teams: [] });
  const { partners = [] } = useSelector((state) => state.partner || { partners: [] });
  const { socket, isConnected } = useWebSocket();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [viewDate, setViewDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const isViewingToday = isSameCalendarDay(viewDate, currentTime);
  const [workersStatus, setWorkersStatus] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const selectedJobIdRef = useRef(null);
  const selectedJobRef = useRef(null);
  const showJobModalRef = useRef(false);
  const businessType = getBusinessType();
  
  // NOTE: Stari sistem za parking podsetnike preko localStorage-a je uklonjen,
  // sada koristimo centralizovani NotificationChecker i backend notifikacije.
  
  // Keep refs in sync with state
  useEffect(() => {
    showJobModalRef.current = showJobModal;
  }, [showJobModal]);
  
  // Keep selectedJobRef in sync with selectedJob
  useEffect(() => {
    selectedJobRef.current = selectedJob;
  }, [selectedJob]);

  // LOG: Component mount/remount - povratak na sekciju
  useEffect(() => {
    const realNowISO = new Date().toISOString();
    
    console.log('🔄 StatusView - POVRATAK NA SEKCIJU (MOUNT/REMOUNT):', {
      timestamp: realNowISO
    });
    
    return () => {
      console.log('🔄 StatusView - NAPUŠTANJE SEKCIJE (UNMOUNT):', {
        timestamp: new Date().toISOString()
      });
    };
  }, []); // Run only on mount/unmount
  
  // Debug: Log when showJobModal changes
  useEffect(() => {
    console.log('🔍 StatusView: showJobModal changed to:', showJobModal, '| Ref:', showJobModalRef.current);
    if (showJobModal === false && showJobModalRef.current === true) {
      console.error('🚨 CRITICAL: showJobModal changed to false! Stack trace:', new Error().stack);
    }
  }, [showJobModal]);
  
  // Fetch workers status from database
  const fetchWorkersStatus = useCallback(async () => {
    try {
      const profile = JSON.parse(localStorage.getItem('profile') || '{}');
      const token = profile?.token;
      if (!token) return;
      
      const response = await axios.get(
        `${API_BASE_URL}/api/worker/status/all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWorkersStatus(response.data || []);
    } catch (error) {
      console.error('Error fetching workers status:', error);
    }
  }, []);
  
  useEffect(() => {
    const realNow = Date.now();
    const realNowISO = new Date().toISOString();
    
    console.log('🔄 StatusView: MOUNT/REMOUNT - Loading jobs:', {
      timestamp: realNowISO,
      realTime: new Date(realNow).toLocaleTimeString('sr-RS'),
      realNowTimestamp: realNow,
      businessType
    });
    
    dispatch(getJobs(businessType));
    dispatch(getWorkers());
    dispatch(getTeams());
    fetchWorkersStatus();
    
    // Refresh workers status every 30 seconds (reduced frequency)
    const statusInterval = setInterval(fetchWorkersStatus, 30000);
    
    // Update time every second for real-time display
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => {
      clearInterval(timer);
      clearInterval(statusInterval);
    };
  }, [dispatch, businessType, fetchWorkersStatus]);

  // WebSocket listener for real-time updates
  useEffect(() => {
    if (socket && isConnected) {
      socket.on('job_updated', (data) => {
        const patch = buildJobUpdatesFromWebSocket(data);
        if (!patch) return;

        dispatch(updateJobInArray(patch));

        const travelActive =
          patch.updates.travelStartTime != null && patch.updates.travelStartTime !== '';
        const workActive =
          patch.updates.workStartTime != null && patch.updates.workStartTime !== '';
        if (travelActive || workActive) {
          setTimerUpdateTrigger((prev) => prev + 1);
        } else if (
          patch.updates.travelStartTime === null ||
          patch.updates.workStartTime === null
        ) {
          setTimerUpdateTrigger(0);
        }

        fetchWorkersStatus();

        if (
          showJobModalRef.current &&
          selectedJobIdRef.current &&
          String(patch.jobId) === String(selectedJobIdRef.current)
        ) {
          setSelectedJob((prev) => {
            const base =
              prev && String(prev._id) === String(patch.jobId)
                ? prev
                : selectedJobRef.current;
            if (!base || String(base._id) !== String(patch.jobId)) return prev;
            const updated = { ...base, ...patch.updates };
            selectedJobRef.current = updated;
            return updated;
          });
        }
      });

      socket.on('job_deleted', (data) => {
        console.log('📋 StatusView: Job deleted via WebSocket:', data);
        if (data?.jobId) dispatch(removeJobById(data.jobId));
        // Close modal if deleted job was selected
        if (showJobModalRef.current && selectedJobIdRef.current && data.jobId === selectedJobIdRef.current) {
          setSelectedJob(null);
          setShowJobModal(false);
          selectedJobIdRef.current = null;
          selectedJobRef.current = null;
          showJobModalRef.current = false;
        }
      });

      socket.on('worker_status_changed', () => {
        fetchWorkersStatus();
      });

      return () => {
        socket.off('job_updated');
        socket.off('job_deleted');
        socket.off('worker_status_changed');
      };
    }
  }, [socket, isConnected, dispatch, fetchWorkersStatus]);
  
  // Removed auto-refresh interval - WebSocket handles all real-time updates
  // Timer display updates in real-time via WebSocket and local state
  // No need for polling since WebSocket provides instant updates
  
  // Real-time timer updates - force re-render every second when timers are active
  const [timerUpdateTrigger, setTimerUpdateTrigger] = useState(0);
  useEffect(() => {
    // Check if any job has active timers
    // IMPORTANT: Check that travelStartTime/workStartTime are NOT null AND status matches (timer is active)
    const hasActiveTimers = jobs?.some(isJobTimerActive);
    
    console.log('⏱️ StatusView: Checking for active timers', {
      hasActiveTimers,
      jobsCount: jobs?.length,
      activeJobs: getJobsWithActiveTimer(jobs).map(j => ({
        id: j._id,
        status: j.status,
        travelStartTime: j.travelStartTime,
        workStartTime: j.workStartTime
      })),
      timestamp: new Date().toISOString()
    });
    
    if (hasActiveTimers) {
      // Update every second to show live timer counts ONLY when timers are active
      const interval = setInterval(() => {
        setTimerUpdateTrigger(prev => prev + 1);
      }, 1000);
      
      return () => clearInterval(interval);
    } else {
      // Reset trigger when no active timers (prevents unnecessary re-renders)
      setTimerUpdateTrigger(0);
    }
  }, [jobs]); // Don't include timerUpdateTrigger to avoid infinite loop
  
  // Update selectedJob when jobs are refreshed (only if modal is open)
  // Use a debounce or check to prevent too frequent updates
  useEffect(() => {
    const refreshTime = new Date();
    console.log('🔄 StatusView: useEffect triggered - jobs changed', {
      timestamp: refreshTime.toISOString(),
      realTime: refreshTime.toLocaleTimeString('sr-RS'),
      showJobModal,
      showJobModalRef: showJobModalRef.current,
      selectedJobIdRef: selectedJobIdRef.current,
      jobsLength: jobs?.length
    });
    
    // Use ref to avoid stale closure issues
    // CRITICAL: Only update if modal is actually open (check both state and ref)
    // IMPORTANT: Must check showJobModal state explicitly - don't rely only on ref
    if (showJobModal && showJobModalRef.current && selectedJobIdRef.current && jobs) {
      const updatedJob = jobs.find(j => j._id === selectedJobIdRef.current);
      if (updatedJob) {
        // Only update if the job data actually changed (to prevent unnecessary re-renders)
        const currentJob = selectedJobRef.current || selectedJob;
        if (!currentJob || 
            currentJob.travelStartTime !== updatedJob.travelStartTime ||
            currentJob.workStartTime !== updatedJob.workStartTime ||
            currentJob.accumulatedTravelTime !== updatedJob.accumulatedTravelTime ||
            currentJob.accumulatedWorkTime !== updatedJob.accumulatedWorkTime ||
            currentJob.status !== updatedJob.status) {
          // Only update if timer data or status changed
          // Use functional update to ensure we have the latest state
          setSelectedJob(prev => {
            // Double-check that modal is still open before updating
            // CRITICAL: Check both state and ref to prevent auto-opening modal
            if (!showJobModal || !showJobModalRef.current || !selectedJobIdRef.current) {
              console.log('⚠️ StatusView: Skipping selectedJob update - modal is closed', {
                showJobModal,
                showJobModalRef: showJobModalRef.current,
                selectedJobIdRef: selectedJobIdRef.current
              });
              return prev; // Don't update if modal was closed
            }
            console.log('✅ StatusView: Updating selectedJob with timer data');
            return updatedJob;
          });
        }
      } else {
        // If job is not found, keep the existing selectedJob to prevent modal from closing
        // Don't set to null - this would close the modal
        // Keep the last known job data
        if (!selectedJobRef.current && selectedJobIdRef.current) {
          // If we lost the job but modal is still open, try to find it in the next render
          console.log('Job not found in refresh, keeping existing selectedJob');
        }
        // IMPORTANT: Don't update selectedJob if job is not found - keep the existing one
      }
    } else {
      // CRITICAL: If modal is NOT open, clear selectedJob to prevent auto-opening
      // This prevents modal from opening when job is updated but modal was never opened
      if (selectedJob && !showJobModal && !showJobModalRef.current) {
        console.log('🧹 StatusView: Clearing selectedJob - modal is not open');
        setSelectedJob(null);
        selectedJobRef.current = null;
        selectedJobIdRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Note: selectedJob is intentionally excluded to prevent infinite loops
    // We use selectedJobRef.current instead for comparison
  }, [jobs, showJobModal, selectedJob]);
  
  const calculateStatistics = (viewMode = 'all', referenceDate = viewDate) => {
    const today = new Date(referenceDate);
    today.setHours(0, 0, 0, 0);
    let allJobs = jobs || [];

    // CRITICAL: Filter out Completed, Cancelled, Finished, and Archived jobs - only show active jobs in "Aktivno" section
    let filteredJobs = allJobs.filter(job => 
      job.status !== 'Completed' && 
      job.status !== 'Cancelled' && 
      job.status !== 'Finished' && 
      job.status !== 'Archived'
    );

    if (viewMode === 'day') {
      filteredJobs = filteredJobs.filter(job => {
        if (!job.serviceDate) return false;
        const jobDate = new Date(job.serviceDate);
        return jobDate.toDateString() === today.toDateString();
      });
    } else if (viewMode === 'week') {
      const startOfWeek = new Date(today);
      const day = today.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      startOfWeek.setDate(today.getDate() + diff);
      startOfWeek.setHours(0, 0, 0, 0);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);

      filteredJobs = filteredJobs.filter(job => {
        if (!job.serviceDate) return false;
        const jobDate = new Date(job.serviceDate);
        return jobDate >= startOfWeek && jobDate <= endOfWeek;
      });
    } else if (viewMode === 'month') {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      filteredJobs = filteredJobs.filter(job => {
        if (!job.serviceDate) return false;
        const jobDate = new Date(job.serviceDate);
        return jobDate >= startOfMonth && jobDate <= endOfMonth;
      });
    }

    const horizon = countScheduledJobsByHorizon(allJobs, today);
    const zakazano =
      horizon.danas +
      horizon.sutra +
      horizon.zaDvaDana +
      horizon.sledecaNedelja +
      horizon.ostalo;
    const ukupno = zakazano + horizon.naCekanju;
    
    return {
      ukupno,
      zakazano,
      nezakazani: horizon.naCekanju,
      danas: horizon.danas,
      sutra: horizon.sutra,
      zaDvaDana: horizon.zaDvaDana,
      sledecaNedelja: horizon.sledecaNedelja,
      ostalo: horizon.ostalo
    };
  };

  const stats = useMemo(
    () => calculateStatistics('all', viewDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jobs, viewDate]
  );
  
  // Additional calculations for urgent jobs and other metrics - ONLY ACTIVE JOBS
  const urgentJobs = jobs?.filter(job => 
    job.priority === 'Urgent' && 
    job.status !== 'Completed' && 
    job.status !== 'Cancelled' &&
    job.status !== 'Finished' &&
    job.status !== 'Archived'
  ) || [];
  const getWorkerCurrentJob = (workerId) => {
    // Find job that is currently active (In Repair, In Progress, On Road, or Diagnosing)
    // IMPORTANT: For "On Road" status, also check that travelStartTime is not null (timer is active)
    // IMPORTANT: For "In Repair" or "In Progress" status, also check that workStartTime is not null (timer is active)
    // Use isJobAssignedToWorker to handle different assignedTo formats (string, object, with prefix)
    const activeJob = jobs?.find(job =>
      isJobAssignedToWorker(job, workerId) && isJobTimerActive(job)
    );
    
    // LOG: Get current job
    if (activeJob) {
      console.log('📋 getWorkerCurrentJob - Found active job:', {
        jobId: activeJob._id,
        clientName: activeJob.clientName,
        status: activeJob.status,
        travelStartTime: activeJob.travelStartTime,
        workStartTime: activeJob.workStartTime,
        accumulatedTravelTime: activeJob.accumulatedTravelTime,
        accumulatedWorkTime: activeJob.accumulatedWorkTime,
        timestamp: new Date().toISOString()
      });
    }
    
    return activeJob;
  };
  
  const getWorkerStatus = (worker, currentJob) => {
    // First check database worker status (most accurate)
    const workerStatusFromDB = workersStatus.find(ws => ws.workerId?._id === worker._id || ws.workerId === worker._id);
    if (workerStatusFromDB?.status) {
      // Map database statuses to UI statuses
      const statusMap = {
        'on_the_road': 'on_the_road',
        'at_client': 'at_client',
        'on_break': 'on_break',
        'offline': 'offline',
        'available': 'available',
        'completed': 'available'
      };
      const mappedStatus = statusMap[workerStatusFromDB.status];
      if (mappedStatus) return mappedStatus;
    }
    
    // Fallback: check if worker has a manual status set
    if (worker.currentStatus === 'on_break') {
      return 'on_break';
    }
    if (worker.currentStatus === 'offline') {
      return 'offline';
    }
    
    if (!currentJob) {
      const hasScheduled = jobs?.some(job => 
        isJobAssignedToWorker(job, worker._id) && 
        job.serviceDate && 
        new Date(job.serviceDate) > new Date()
      );
      return hasScheduled ? 'scheduled' : 'available';
    }
    
    // Fallback: determine worker status from job.status first
    // (serviceLocation can be "OnSite" even while the job is still "On Road")
    if (currentJob.status === 'On Road') {
      return 'on_the_road';
    }
    if (currentJob.status === 'At Client') {
      return 'at_client';
    }

    // For other active job states, decide based on workshop vs on-site.
    // Keep serviceLocation as a last resort.
    if (currentJob.serviceLocation === 'OnSite') {
      return 'at_client';
    }

    return 'in_workshop';
  };

  const getStatusFromJob = (currentJob, hasScheduled = false) => {
    if (!currentJob) return hasScheduled ? 'scheduled' : 'available';
    if (currentJob.status === 'On Road') return 'on_the_road';
    if (currentJob.status === 'At Client') return 'at_client';
    if (currentJob.serviceLocation === 'OnSite') return 'at_client';
    return 'in_workshop';
  };

  const openJobDetails = (job) => {
    const latestJob = jobs?.find((j) => j._id === job._id) || job;
    selectedJobIdRef.current = latestJob._id;
    selectedJobRef.current = latestJob;
    showJobModalRef.current = true;
    setSelectedJob(latestJob);
    setShowJobModal(true);
  };

  const statusConfig = {
    available: { icon: UserCheck, label: 'Dostupan', color: 'bg-green-100 text-green-800', iconColor: 'text-green-600' },
    scheduled: { icon: Clock, label: 'Zakazan', color: 'bg-blue-100 text-blue-800', iconColor: 'text-blue-600' },
    on_the_road: { icon: MapPin, label: 'U putu', color: 'bg-blue-100 text-blue-800', iconColor: 'text-blue-600' },
    in_workshop: { icon: Wrench, label: 'U servisu', color: 'bg-purple-100 text-purple-800', iconColor: 'text-purple-600' },
    at_client: { icon: Wrench, label: 'Kod stranke', color: 'bg-orange-100 text-orange-800', iconColor: 'text-orange-600' },
    on_break: { icon: Coffee, label: 'Na pauzi', color: 'bg-gray-100 text-gray-800', iconColor: 'text-gray-600' },
    offline: { icon: AlertCircle, label: 'Offline', color: 'bg-red-100 text-red-800', iconColor: 'text-red-600' }
  };

  const CurrentJobBlock = ({ currentJob }) => {
    if (!currentJob) return null;

    return (
      <div
        className="mb-3 p-2 sm:p-3 bg-gray-700 rounded-md border-l-2 border-orange-500 cursor-pointer hover:bg-gray-600 transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          openJobDetails(currentJob);
        }}
      >
        <p className="text-xs text-gray-400 mb-1">Trenutni posao:</p>
        <p className="font-medium text-xs sm:text-sm text-white">{currentJob.clientName}</p>
        <p className="text-xs text-gray-400">{currentJob.clientAddress || currentJob.clientPhone}</p>
        <p className="text-xs text-gray-500 mt-1">
          {formatDisplayValue(currentJob.issueDescription) !== '-'
            ? `${formatDisplayValue(currentJob.issueDescription).substring(0, 50)}...`
            : '-'}
        </p>
        {(currentJob.travelStartTime ||
          currentJob.workStartTime ||
          currentJob.accumulatedTravelTime > 0 ||
          currentJob.accumulatedWorkTime > 0) && (
          <div className="mt-2 pt-2 border-t border-gray-600">
            <p className="text-xs text-gray-400 mb-1">Tajmeri:</p>
            {((currentJob.travelStartTime !== null &&
              currentJob.travelStartTime !== undefined &&
              currentJob.status === 'On Road') ||
              (currentJob.accumulatedTravelTime > 0 && !currentJob.travelStartTime)) && (
              <div className="flex items-center gap-2 text-xs">
                <Clock className="h-3 w-3 text-blue-400" />
                <span className="text-gray-300">Putovanje:</span>
                <span className="text-blue-400 font-medium">
                  {(() => {
                    const startTime = currentJob.travelStartTime ? new Date(currentJob.travelStartTime) : null;
                    const now = new Date();
                    const accumulated = currentJob.accumulatedTravelTime || 0;
                    const isTimerActive =
                      currentJob.status === 'On Road' &&
                      currentJob.travelStartTime !== null &&
                      currentJob.travelStartTime !== undefined &&
                      startTime !== null;
                    let totalElapsed;
                    if (isTimerActive && startTime) {
                      totalElapsed = accumulated + Math.floor((now.getTime() - startTime.getTime()) / 1000);
                    } else {
                      totalElapsed = accumulated;
                    }
                    const hours = Math.floor(totalElapsed / 3600);
                    const minutes = Math.floor((totalElapsed % 3600) / 60);
                    const seconds = totalElapsed % 60;
                    return `${hours.toString().padStart(2, '0')}:${minutes
                      .toString()
                      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                  })()}
                </span>
              </div>
            )}
            {((currentJob.workStartTime !== null &&
              currentJob.workStartTime !== undefined &&
              (currentJob.status === 'In Repair' || currentJob.status === 'In Progress')) ||
              (currentJob.accumulatedWorkTime > 0 && !currentJob.workStartTime)) && (
              <div className="flex items-center gap-2 text-xs mt-1">
                <Wrench className="h-3 w-3 text-purple-400" />
                <span className="text-gray-300">Rad:</span>
                <span className="text-purple-400 font-medium">
                  {(() => {
                    const startTime = currentJob.workStartTime ? new Date(currentJob.workStartTime) : null;
                    const now = new Date();
                    const accumulated = currentJob.accumulatedWorkTime || 0;
                    const isTimerActive =
                      (currentJob.status === 'In Repair' || currentJob.status === 'In Progress') &&
                      currentJob.workStartTime !== null &&
                      currentJob.workStartTime !== undefined &&
                      startTime !== null;
                    let totalElapsed;
                    if (isTimerActive && startTime) {
                      totalElapsed = accumulated + Math.floor((now.getTime() - startTime.getTime()) / 1000);
                    } else {
                      totalElapsed = accumulated;
                    }
                    const hours = Math.floor(totalElapsed / 3600);
                    const minutes = Math.floor((totalElapsed % 3600) / 60);
                    const seconds = totalElapsed % 60;
                    return `${hours.toString().padStart(2, '0')}:${minutes
                      .toString()
                      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                  })()}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const activeWorkers = useMemo(
    () => (workers || []).filter((worker) => worker?.active !== false),
    [workers]
  );

  const activeTeams = useMemo(
    () => (teams || []).filter((team) => team?.active !== false),
    [teams]
  );

  const teamDailyLoad = useMemo(
    () => summarizeTeamDailyLoad(workers, jobs, viewDate),
    [workers, jobs, viewDate]
  );

  const workerStats = useMemo(() => {
    const base = summarizeWorkerStatuses(
      workers,
      (worker) => {
        if (!isViewingToday) {
          const hasJobOnDate = jobs?.some(
            (job) =>
              isJobAssignedToWorker(job, worker._id) &&
              job.serviceDate &&
              isSameCalendarDay(job.serviceDate, viewDate)
          );
          return hasJobOnDate ? 'scheduled' : 'available';
        }
        const currentJob = jobs?.find(
          (job) => isJobAssignedToWorker(job, worker._id) && isJobTimerActive(job)
        );
        return getWorkerStatus(worker, currentJob);
      },
      viewDate
    );
    return {
      ...base,
      optereceni: countOverloadedWorkers(jobs, workers, viewDate)
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workers, jobs, workersStatus, timerUpdateTrigger, viewDate, isViewingToday]);
  
  const StatMetric = ({ icon: Icon, label, value, valueClass, iconClass, iconWrapClass, nested = false }) => (
    <div className={`flex min-w-0 w-full sm:flex-1 items-center gap-1.5 ${nested ? '' : 'rounded-lg bg-gray-900/50 border border-gray-700/60 px-2 py-1.5'}`}>
      <div className={`flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${iconWrapClass}`}>
        <Icon className={`h-3 w-3 ${iconClass}`} strokeWidth={2.25} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] text-gray-400 leading-snug truncate">{label}</p>
        <p className={`text-base sm:text-lg font-bold tabular-nums leading-none mt-0.5 ${valueClass}`}>{value}</p>
      </div>
    </div>
  );

  const StatsBar = ({ metrics, className = '' }) => (
    <div className={`rounded-lg border border-gray-700/60 bg-gray-900/35 px-2 py-1.5 h-full ${className}`}>
      <div className="grid grid-cols-3 gap-2 h-full sm:flex sm:flex-nowrap sm:items-stretch">
        {metrics.map((metric) => (
          <StatMetric key={metric.label} nested {...metric} />
        ))}
      </div>
    </div>
  );

  const WorkerCard = ({ worker }) => {
    // Use timerUpdateTrigger to force re-render ONLY when timers are active
    // This ensures timer display updates every second without full component re-render
    const _ = timerUpdateTrigger;
    
    // Memoize current job and sync with jobs array for real-time updates
    // CRITICAL: Always get latest job directly from jobs array (not from getWorkerCurrentJob)
    // This ensures we always have the most up-to-date data from Redux store
    const currentJob = useMemo(() => {
      if (!isViewingToday) return null;
      const latestJob = jobs?.find(
        (j) => isJobAssignedToWorker(j, worker._id) && isJobTimerActive(j)
      );
      
      if (latestJob) {
        console.log('🔄 WorkerCard: currentJob from jobs array', {
          workerId: worker._id,
          workerName: `${worker.firstName} ${worker.lastName}`,
          jobId: latestJob._id,
          clientName: latestJob.clientName,
          status: latestJob.status,
          travelStartTime: latestJob.travelStartTime,
          workStartTime: latestJob.workStartTime,
          isTravelActive: latestJob.status === 'On Road' && latestJob.travelStartTime !== null,
          isWorkActive: (latestJob.status === 'In Repair' || latestJob.status === 'In Progress') && latestJob.workStartTime !== null,
          timestamp: new Date().toISOString()
        });
      }
      
      return latestJob || null;
    }, [jobs, worker._id, workersStatus, timerUpdateTrigger, isViewingToday]);
    
    const status = isViewingToday
      ? getWorkerStatus(worker, currentJob)
      : (() => {
          const hasJobOnDate = jobs?.some(
            (job) =>
              isJobAssignedToWorker(job, worker._id) &&
              job.serviceDate &&
              isSameCalendarDay(job.serviceDate, viewDate)
          );
          return hasJobOnDate ? 'scheduled' : 'available';
        })();

    const viewDateKey = formatDateKey(viewDate);
    const dayAssignmentCount = useMemo(
      () => countWorkerDayAssignments(jobs, worker._id, viewDate),
      [jobs, worker._id, viewDateKey]
    );
    const maxDailyInterventions = getWorkerMaxDailyInterventions(worker);
    const loadColorClass = getWorkerLoadColorClass(dayAssignmentCount, maxDailyInterventions);

    const config = statusConfig[status] || statusConfig.available;
    const StatusIcon = config.icon;

    return (
      <div className="bg-gray-800 rounded-lg shadow-md p-3 sm:p-4 hover:shadow-lg transition-shadow border border-gray-700/40">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">
              {worker.firstName?.charAt(0)}{worker.lastName?.charAt(0)}
            </div>
            <div>
              <h4 className="font-semibold text-sm sm:text-base text-white">{worker.firstName} {worker.lastName}</h4>
              <p
                className={`text-xs font-bold tabular-nums mt-0.5 ${loadColorClass}`}
                title="Dodeljeni poslovi danas / maksimalan broj intervencija po danu"
              >
                {dayAssignmentCount}/{maxDailyInterventions}
              </p>
              <p className="text-xs text-gray-500">{worker.specialization || 'Tehničar'}</p>
            </div>
          </div>
          <div className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${config.color} flex items-center gap-1`}>
            <StatusIcon className={`h-3 w-3 ${config.iconColor}`} />
            <span className="hidden sm:inline">{config.label}</span>
          </div>
        </div>

        <CurrentJobBlock currentJob={currentJob} />
      </div>
    );
  };

  const TeamCard = ({ team }) => {
    const _ = timerUpdateTrigger;

    const currentJob = useMemo(() => {
      if (!isViewingToday) return null;
      return (
        jobs?.find((j) => isJobAssignedToTeam(j, team._id) && isJobTimerActive(j)) || null
      );
    }, [jobs, team._id, timerUpdateTrigger, isViewingToday]);

    const status = isViewingToday
      ? getStatusFromJob(
          currentJob,
          jobs?.some(
            (job) =>
              isJobAssignedToTeam(job, team._id) &&
              job.serviceDate &&
              new Date(job.serviceDate) > new Date()
          )
        )
      : (() => {
          const hasJobOnDate = jobs?.some(
            (job) =>
              isJobAssignedToTeam(job, team._id) &&
              job.serviceDate &&
              isSameCalendarDay(job.serviceDate, viewDate)
          );
          return hasJobOnDate ? 'scheduled' : 'available';
        })();

    const viewDateKey = formatDateKey(viewDate);
    const dayAssignmentCount = useMemo(
      () => countTeamDayAssignments(jobs, team._id, viewDate),
      [jobs, team._id, viewDateKey]
    );
    const maxDailyInterventions = getTeamMaxDailyInterventions(team, workers, viewDate);
    const loadColorClass = getWorkerLoadColorClass(dayAssignmentCount, maxDailyInterventions);
    const memberCount = getTeamMemberCount(team);
    const teamInitials = (team.name || 'TM').substring(0, 2).toUpperCase();

    const config = statusConfig[status] || statusConfig.available;
    const StatusIcon = config.icon;

    return (
      <div className="bg-gray-800 rounded-lg shadow-md p-3 sm:p-4 hover:shadow-lg transition-shadow border border-violet-500/30">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-violet-500 to-fuchsia-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">
              {teamInitials}
            </div>
            <div>
              <h4 className="font-semibold text-sm sm:text-base text-white">{team.name}</h4>
              <p
                className={`text-xs font-bold tabular-nums mt-0.5 ${loadColorClass}`}
                title="Dodeljeni poslovi timu danas / zbir kapaciteta članova"
              >
                {dayAssignmentCount}/{maxDailyInterventions || '—'}
              </p>
              <p className="text-xs text-gray-500">
                {memberCount === 1 ? '1 član' : `${memberCount} članova`}
              </p>
            </div>
          </div>
          <div className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${config.color} flex items-center gap-1`}>
            <StatusIcon className={`h-3 w-3 ${config.iconColor}`} />
            <span className="hidden sm:inline">{config.label}</span>
          </div>
        </div>

        <CurrentJobBlock currentJob={currentJob} />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return (
    <div className='w-full p-2 sm:p-4 bg-gray-900 min-h-screen'>
      {/* Header */}
      <div className="bg-gray-800 text-white px-4 sm:px-6 py-3 sm:py-4 mb-4 sm:mb-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 min-w-0">
            <Activity className="h-5 w-5 flex-shrink-0" />
            <h2 className="text-lg font-bold truncate">{t('nav.status')}</h2>
            <PulsingIndicator size={8} color="#2ECC71" />
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-xl sm:text-2xl font-bold tabular-nums tracking-tight text-white">
              {currentTime.toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' })}
            </p>
            <div className="flex items-center justify-end gap-2 mt-1">
              <DatePickerField
                value={formatDateKey(viewDate)}
                onChange={(dateStr) => {
                  if (!dateStr) return;
                  const [year, month, day] = dateStr.split('-').map(Number);
                  const next = new Date(year, month - 1, day);
                  next.setHours(0, 0, 0, 0);
                  setViewDate(next);
                }}
                inputClass="w-[7.5rem] sm:w-[8.5rem] rounded-md border border-gray-600 bg-gray-700 text-white text-[11px] sm:text-xs px-2 py-1 text-right focus:border-blue-500 focus:ring-blue-500"
                placeholder="dd.mm.gggg"
              />
              {!isViewingToday && (
                <button
                  type="button"
                  onClick={() => {
                    const d = new Date();
                    d.setHours(0, 0, 0, 0);
                    setViewDate(d);
                  }}
                  className="text-[10px] sm:text-xs text-blue-400 hover:text-blue-300 whitespace-nowrap"
                >
                  Danas
                </button>
              )}
            </div>
            <p className="text-[11px] sm:text-xs text-gray-400 capitalize leading-snug mt-0.5">
              {viewDate.toLocaleDateString('sr-RS', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              {!isViewingToday && (
                <span className="text-amber-400/90 normal-case"> · pregled za izabrani dan</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Poslovi */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-800/90 text-white rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 shadow-lg border border-gray-700/50">
        <h3 className="text-sm sm:text-base font-bold text-white mb-2">Poslovi</h3>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
          <StatsBar
            className="sm:flex-1 sm:min-w-0"
            metrics={[
              {
                icon: ClipboardList,
                label: 'Ukupno',
                value: stats.ukupno,
                valueClass: 'text-white',
                iconClass: 'text-slate-300',
                iconWrapClass: 'bg-slate-500/15 border border-slate-500/20'
              },
              {
                icon: CalendarClock,
                label: 'Zakazano',
                value: stats.zakazano,
                valueClass: 'text-purple-300',
                iconClass: 'text-purple-400',
                iconWrapClass: 'bg-purple-500/15 border border-purple-500/25'
              },
              {
                icon: Coffee,
                label: 'Nezakazano',
                value: stats.nezakazani,
                valueClass: 'text-amber-300',
                iconClass: 'text-amber-400',
                iconWrapClass: 'bg-amber-500/15 border border-amber-500/25'
              }
            ]}
          />
          <StatsBar
            className="sm:flex-[2] sm:min-w-0"
            metrics={[
              {
                icon: CalendarDays,
                label: 'Danas',
                value: stats.danas,
                valueClass: 'text-cyan-300',
                iconClass: 'text-cyan-400',
                iconWrapClass: 'bg-cyan-500/15 border border-cyan-500/25'
              },
              {
                icon: Sun,
                label: 'Sutra',
                value: stats.sutra,
                valueClass: 'text-sky-300',
                iconClass: 'text-sky-400',
                iconWrapClass: 'bg-sky-500/15 border border-sky-500/25'
              },
              {
                icon: CalendarClock,
                label: 'Za dva dana',
                value: stats.zaDvaDana,
                valueClass: 'text-indigo-300',
                iconClass: 'text-indigo-400',
                iconWrapClass: 'bg-indigo-500/15 border border-indigo-500/25'
              },
              {
                icon: CalendarRange,
                label: 'Sledeća nedelja',
                value: stats.sledecaNedelja,
                valueClass: 'text-violet-300',
                iconClass: 'text-violet-400',
                iconWrapClass: 'bg-violet-500/15 border border-violet-500/25'
              },
              {
                icon: MoreHorizontal,
                label: 'Ostalo',
                value: stats.ostalo,
                valueClass: 'text-slate-300',
                iconClass: 'text-slate-400',
                iconWrapClass: 'bg-slate-500/15 border border-slate-500/20'
              },
              {
                icon: Activity,
                label: 'U toku',
                value: workerStats.uVoznji + workerStats.uRadu,
                valueClass: 'text-blue-400',
                iconClass: 'text-blue-400',
                iconWrapClass: 'bg-blue-500/15 border border-blue-500/25'
              }
            ]}
          />
        </div>
      </div>

      {/* Radnici i timovi */}
      <div className="mb-4 sm:mb-6">
        <div className="bg-gradient-to-br from-gray-800 to-gray-800/90 text-white rounded-xl p-3 sm:p-4 mb-3 sm:mb-4 shadow-lg border border-gray-700/50">
          <h3 className="text-sm sm:text-base font-bold text-white mb-2">Radnici i timovi</h3>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
            <StatsBar
              className="sm:flex-1 sm:min-w-0"
              metrics={[
                {
                  icon: Users,
                  label: 'Ukupno',
                  value: activeWorkers.length + activeTeams.length,
                  valueClass: 'text-white',
                  iconClass: 'text-slate-300',
                  iconWrapClass: 'bg-slate-500/15 border border-slate-500/20'
                },
                {
                  icon: UserCheck,
                  label: 'Radnici',
                  value: activeWorkers.length,
                  valueClass: 'text-cyan-300',
                  iconClass: 'text-cyan-400',
                  iconWrapClass: 'bg-cyan-500/15 border border-cyan-500/25'
                },
                {
                  icon: Users2,
                  label: 'Timovi',
                  value: activeTeams.length,
                  valueClass: 'text-violet-300',
                  iconClass: 'text-violet-400',
                  iconWrapClass: 'bg-violet-500/15 border border-violet-500/25'
                }
              ]}
            />
            <StatsBar
              className="sm:flex-[2] sm:min-w-0"
              metrics={[
                {
                  icon: Bed,
                  label: 'Neaktivni',
                  value: workerStats.neaktivni,
                  valueClass: 'text-rose-300',
                  iconClass: 'text-rose-400',
                  iconWrapClass: 'bg-rose-500/15 border border-rose-500/25'
                },
                {
                  icon: UserCheck,
                  label: 'Aktivni',
                  value: workerStats.aktivni,
                  valueClass: 'text-green-300',
                  iconClass: 'text-green-400',
                  iconWrapClass: 'bg-green-500/15 border border-green-500/25'
                },
                {
                  icon: AlertTriangle,
                  label: 'Opterećeni >90%',
                  value: workerStats.optereceni,
                  valueClass: 'text-red-300',
                  iconClass: 'text-red-400',
                  iconWrapClass: 'bg-red-500/15 border border-red-500/25'
                },
                {
                  icon: Coffee,
                  label: 'Na pauzi',
                  value: workerStats.naPauzi,
                  valueClass: 'text-amber-300',
                  iconClass: 'text-amber-400',
                  iconWrapClass: 'bg-amber-500/15 border border-amber-500/25'
                },
                {
                  icon: Car,
                  label: 'U vožnji',
                  value: workerStats.uVoznji,
                  valueClass: 'text-blue-300',
                  iconClass: 'text-blue-400',
                  iconWrapClass: 'bg-blue-500/15 border border-blue-500/25'
                },
                {
                  icon: Wrench,
                  label: 'U radu',
                  value: workerStats.uRadu,
                  valueClass: 'text-indigo-300',
                  iconClass: 'text-indigo-400',
                  iconWrapClass: 'bg-indigo-500/15 border border-indigo-500/25'
                }
              ]}
            />
          </div>
        </div>

        {activeWorkers.length > 0 || activeTeams.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {activeWorkers.map((worker) => (
              <WorkerCard key={`worker-${worker._id}`} worker={worker} />
            ))}
            {activeTeams.map((team) => (
              <TeamCard key={`team-${team._id}`} team={team} />
            ))}
          </div>
        ) : (
          <div className="bg-gray-800 rounded-lg p-6 sm:p-8 text-center text-gray-500">
            Nema aktivnih radnika ili timova u sistemu
          </div>
        )}
      </div>
      
      {/* Urgent Jobs Alert */}
      {urgentJobs.length > 0 && (
        <div className="bg-red-900/20 border-l-4 border-red-500 p-3 sm:p-4 mb-4 sm:mb-6 rounded-r-lg">
          <div className="flex items-center gap-2 sm:gap-3">
            <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-500" />
            <div>
              <h3 className="font-bold text-sm sm:text-base text-red-400">Hitni poslovi: {urgentJobs.length}</h3>
              <p className="text-xs sm:text-sm text-red-300">
                {urgentJobs.slice(0, 3).map(j => j.clientName).join(', ')}
                {urgentJobs.length > 3 && ` i još ${urgentJobs.length - 3}`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Dnevno opterećenje tima */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-800/90 text-white rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 shadow-lg border border-gray-700/50">
        <div className="flex items-center justify-between gap-3 mb-2">
          <h3 className="text-sm sm:text-base font-bold text-white">Dnevno opterećenje tima</h3>
          <p
            className={`text-lg sm:text-xl font-bold tabular-nums ${getWorkerLoadColorClass(
              teamDailyLoad.used,
              teamDailyLoad.capacity
            )}`}
          >
            {teamDailyLoad.used}/{teamDailyLoad.capacity}
          </p>
        </div>
        <div className="h-2.5 w-full rounded-full bg-gray-900/80 border border-gray-700/60 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${getWorkerLoadBarColorClass(
              teamDailyLoad.used,
              teamDailyLoad.capacity
            )}`}
            style={{
              width: `${teamDailyLoad.capacity > 0
                ? Math.min(100, (teamDailyLoad.used / teamDailyLoad.capacity) * 100)
                : 0}%`
            }}
          />
        </div>
        <p className="text-[10px] sm:text-xs text-gray-400 mt-1.5">
          Zakazani poslovi danas / zbir kapaciteta dostupnih radnika
        </p>
      </div>
      
      {/* Job Details Modal */}
      {/* CRITICAL: Only show modal if showJobModal is explicitly true - prevents auto-opening */}
      {showJobModal && selectedJob && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              console.log('🔒 StatusView: User clicked outside modal - closing');
              console.log('🔒 StatusView: showJobModal BEFORE:', showJobModal);
              selectedJobIdRef.current = null;
              selectedJobRef.current = null;
              showJobModalRef.current = false;
              setShowJobModal(false); // Allow closing - user explicitly clicked outside
              console.log('🔒 StatusView: showJobModal AFTER setShowJobModal(false)');
              setSelectedJob(null);
            }
          }}
        >
          <div 
            className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                console.log('🔒 StatusView: User clicked X button - closing');
                console.log('🔒 StatusView: showJobModal BEFORE:', showJobModal);
                selectedJobIdRef.current = null;
                selectedJobRef.current = null;
                showJobModalRef.current = false;
                setShowJobModal(false);
                console.log('🔒 StatusView: showJobModal AFTER setShowJobModal(false)');
                setSelectedJob(null);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <X className="h-6 w-6" />
            </button>
            
            <h2 className="text-xl font-bold text-white mb-4">Detalji Posla</h2>
            
            {(() => {
              // Use timerUpdateTrigger to force re-render when timers update
              const _ = timerUpdateTrigger;
              // Always get the latest job from jobs array to ensure real-time updates
              const latestJob = jobs?.find(j => j._id === (selectedJob?._id || selectedJobRef.current?._id || selectedJobIdRef.current)) || selectedJob || selectedJobRef.current;
              const jobToDisplay = latestJob;
              if (!jobToDisplay) {
                return (
                  <div className="text-center p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-400">Učitavanje detalja posla...</p>
                  </div>
                );
              }
              
              return (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Klijent:</p>
                    <p className="text-white font-medium">{jobToDisplay.clientName}</p>
                  </div>
                  
                  {jobToDisplay.clientAddress && (
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Adresa:</p>
                      <p className="text-white">{jobToDisplay.clientAddress}</p>
                    </div>
                  )}
                  
                  {jobToDisplay.clientPhone && (
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Telefon:</p>
                      <p className="text-white">{jobToDisplay.clientPhone}</p>
                    </div>
                  )}
                  
                  {jobToDisplay.issueDescription && (
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Opis problema:</p>
                      <p className="text-white">{jobToDisplay.issueDescription}</p>
                    </div>
                  )}
                  
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Status:</p>
                    <p className="text-white">{getJobDisplayStatus(jobToDisplay)}</p>
                  </div>
                  
                  {/* Timer Information - Real-time synchronized */}
                  {(jobToDisplay.travelStartTime || jobToDisplay.workStartTime || jobToDisplay.accumulatedTravelTime > 0 || jobToDisplay.accumulatedWorkTime > 0) && (
                    <div className="mt-4 pt-4 border-t border-gray-700">
                      <p className="text-sm font-semibold text-gray-300 mb-3">Tajmeri:</p>
                      
                      {/* Show travel timer only if it's active OR if there's accumulated time but timer is paused */}
                      {((jobToDisplay.travelStartTime && jobToDisplay.status === 'On Road') || (jobToDisplay.accumulatedTravelTime > 0 && !jobToDisplay.travelStartTime)) && (
                        <div className="mb-3 p-3 bg-gray-700 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <MapPin className="h-4 w-4 text-blue-400" />
                            <span className="text-sm font-medium text-gray-300">Putovanje:</span>
                          </div>
                          <div className="text-2xl font-mono text-blue-400">
                            {(() => {
                              const startTime = jobToDisplay.travelStartTime ? new Date(jobToDisplay.travelStartTime) : null;
                              const realNow = Date.now();
                              const realStartTime = startTime ? startTime.getTime() : null;
                              
                              // Check if timer is still active (travelStartTime exists and status is 'On Road')
                              // Timer is active ONLY if travelStartTime is not null AND status matches
                              const isTimerActive = jobToDisplay.status === 'On Road' && 
                                                   jobToDisplay.travelStartTime !== null && 
                                                   jobToDisplay.travelStartTime !== undefined &&
                                                   startTime !== null &&
                                                   realStartTime !== null;
                              
                              // Get accumulated time from DB
                              const accumulatedFromDB = jobToDisplay.accumulatedTravelTime || 0;
                              
                              // Calculate elapsed from startTime to now (only if timer is active)
                              const currentElapsed = isTimerActive && realStartTime ? Math.floor((realNow - realStartTime) / 1000) : 0;
                              
                              // LOG: Detailed timing information (MODAL VIEW)
                              console.log('🕐 TRAVEL TIMER CALCULATION (MODAL):', {
                                jobId: jobToDisplay._id,
                                clientName: jobToDisplay.clientName,
                                status: jobToDisplay.status,
                                isTimerActive,
                                realNow: new Date(realNow).toISOString(),
                                realStartTime: new Date(realStartTime).toISOString(),
                                startTimeString: jobToDisplay.travelStartTime,
                                accumulatedFromDB,
                                currentElapsed,
                                calculatedTotal: isTimerActive ? currentElapsed : accumulatedFromDB,
                                timestamp: new Date().toISOString()
                              });
                              
                              let totalElapsed;
                              if (isTimerActive && startTime) {
                                // If timer is active, we need to show accumulated + current elapsed
                                // accumulated contains time from previous sessions (before current startTime)
                                // currentElapsed is time from current startTime to now
                                totalElapsed = accumulatedFromDB + currentElapsed;
                                console.log('🕐 TRAVEL TIMER (MODAL) - Timer AKTIVAN:', {
                                  accumulatedFromDB,
                                  currentElapsed,
                                  totalElapsed,
                                  note: 'accumulated (iz DB, prethodne sesije) + currentElapsed (od trenutnog startTime do sada)'
                                });
                              } else {
                                // If timer is not active, use accumulated time from DB only (static, doesn't update)
                                totalElapsed = accumulatedFromDB;
                                console.log('🕐 TRAVEL TIMER (MODAL) - Timer NIJE aktivan:', {
                                  totalElapsed,
                                  accumulatedFromDB,
                                  travelStartTime: jobToDisplay.travelStartTime,
                                  status: jobToDisplay.status,
                                  note: 'Timer nije aktivan, koristim samo accumulated (static)'
                                });
                              }
                              
                              const hours = Math.floor(totalElapsed / 3600);
                              const minutes = Math.floor((totalElapsed % 3600) / 60);
                              const seconds = totalElapsed % 60;
                              return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                            })()}
                          </div>
                          {jobToDisplay.travelStartTime && (
                            <p className="text-xs text-gray-500 mt-1">
                              Start: {new Date(jobToDisplay.travelStartTime).toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Show work timer only if it's active OR if there's accumulated time but timer is paused */}
                      {((jobToDisplay.workStartTime && (jobToDisplay.status === 'In Repair' || jobToDisplay.status === 'In Progress')) || (jobToDisplay.accumulatedWorkTime > 0 && !jobToDisplay.workStartTime)) && (
                        <div className="mb-3 p-3 bg-gray-700 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Wrench className="h-4 w-4 text-purple-400" />
                            <span className="text-sm font-medium text-gray-300">Rad:</span>
                          </div>
                          <div className="text-2xl font-mono text-purple-400">
                            {(() => {
                              // If workStartTime exists, use it; otherwise use accumulated time only
                              const startTime = jobToDisplay.workStartTime ? new Date(jobToDisplay.workStartTime) : null;
                              const realNow = Date.now();
                              const realStartTime = startTime ? startTime.getTime() : null;
                              
                              // Check if timer is still active (workStartTime exists and status is 'In Repair' or 'In Progress')
                              // Timer is active ONLY if workStartTime is not null AND status matches
                              const isTimerActive = (jobToDisplay.status === 'In Repair' || jobToDisplay.status === 'In Progress') && 
                                                   jobToDisplay.workStartTime !== null && 
                                                   jobToDisplay.workStartTime !== undefined &&
                                                   startTime !== null &&
                                                   realStartTime !== null;
                              
                              // Get accumulated time from DB
                              const accumulatedFromDB = jobToDisplay.accumulatedWorkTime || 0;
                              
                              // Calculate elapsed from startTime to now (only if timer is active)
                              const currentElapsed = isTimerActive && realStartTime ? Math.floor((realNow - realStartTime) / 1000) : 0;
                              
                              // LOG: Detailed timing information (MODAL VIEW)
                              console.log('🕐 WORK TIMER CALCULATION (MODAL):', {
                                jobId: jobToDisplay._id,
                                clientName: jobToDisplay.clientName,
                                status: jobToDisplay.status,
                                isTimerActive,
                                realNow: new Date(realNow).toISOString(),
                                realStartTime: new Date(realStartTime).toISOString(),
                                startTimeString: jobToDisplay.workStartTime,
                                accumulatedFromDB,
                                currentElapsed,
                                calculatedTotal: isTimerActive ? (accumulatedFromDB + currentElapsed) : accumulatedFromDB,
                                timestamp: new Date().toISOString()
                              });
                              
                              let totalElapsed;
                              if (isTimerActive && startTime && realStartTime) {
                                // If timer is active, we need to show accumulated + current elapsed
                                // accumulated contains time from previous sessions (before current startTime)
                                // currentElapsed is time from current startTime to now
                                totalElapsed = accumulatedFromDB + currentElapsed;
                                console.log('🕐 WORK TIMER (MODAL) - Timer AKTIVAN:', {
                                  accumulatedFromDB,
                                  currentElapsed,
                                  totalElapsed,
                                  note: 'accumulated (iz DB, prethodne sesije) + currentElapsed (od trenutnog startTime do sada)'
                                });
                              } else {
                                // If timer is not active, use accumulated time from DB only (static, doesn't update)
                                totalElapsed = accumulatedFromDB;
                                console.log('🕐 WORK TIMER (MODAL) - Timer NIJE aktivan:', {
                                  totalElapsed,
                                  accumulatedFromDB,
                                  workStartTime: jobToDisplay.workStartTime,
                                  status: jobToDisplay.status,
                                  note: 'Timer nije aktivan, koristim samo accumulated (static)'
                                });
                              }
                              
                              const hours = Math.floor(totalElapsed / 3600);
                              const minutes = Math.floor((totalElapsed % 3600) / 60);
                              const seconds = totalElapsed % 60;
                              return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                            })()}
                          </div>
                          {jobToDisplay.workStartTime && (
                            <p className="text-xs text-gray-500 mt-1">
                              Start: {new Date(jobToDisplay.workStartTime).toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {!jobToDisplay.travelStartTime && !jobToDisplay.workStartTime && (
                        <div className="text-sm text-gray-400">
                          {jobToDisplay.accumulatedTravelTime > 0 && (
                            <p>Putovanje: {Math.floor(jobToDisplay.accumulatedTravelTime / 60)} min</p>
                          )}
                          {jobToDisplay.accumulatedWorkTime > 0 && (
                            <p>Rad: {Math.floor(jobToDisplay.accumulatedWorkTime / 60)} min</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusView;
